export interface OilSpillLegendForecast {
  hour: number;
  color: string;
}

export interface OilSpillLegendGroup {
  id: string;
  label: string;
  color: string;
  forecasts: OilSpillLegendForecast[];
}

function normalizedClass(value: unknown): string | null {
  if (value == null || String(value).trim() === '') return null;
  return String(value).trim();
}

function forecastHour(value: unknown): number | null {
  if (value == null || String(value).trim() === '') return null;
  const hour = Number(value);
  return Number.isFinite(hour) && hour !== 0 ? hour : null;
}

function compareClasses(left: string, right: string): number {
  const numericDifference = Number(left) - Number(right);
  return Number.isNaN(numericDifference)
    ? left.localeCompare(right)
    : numericDifference;
}

export function buildOilSpillLegendGroups(
  features: any[] = [],
  config: any,
  fallbackColor: string,
  idPrefix: string
): OilSpillLegendGroup[] {
  const detectionClasses = new Set<string>();
  const forecastHoursByClass = new Map<string, Set<number>>();
  const unclassifiedForecastHours = new Set<number>();
  let hasUnclassifiedDetection = false;

  for (const feature of features ?? []) {
    const geometryType = feature?.geometry?.type;
    const detectionClass = normalizedClass(feature?.properties?.class);

    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      if (detectionClass) detectionClasses.add(detectionClass);
      else hasUnclassifiedDetection = true;
      continue;
    }

    if (geometryType !== 'Point' && geometryType !== 'MultiPoint') continue;

    const hour = forecastHour(
      feature?.properties?.forecast_h ?? feature?.properties?.forecast_hour
    );
    if (hour == null) continue;

    if (!detectionClass) {
      unclassifiedForecastHours.add(hour);
      continue;
    }

    detectionClasses.add(detectionClass);
    if (!forecastHoursByClass.has(detectionClass)) {
      forecastHoursByClass.set(detectionClass, new Set<number>());
    }
    forecastHoursByClass.get(detectionClass)!.add(hour);
  }

  const groups = [...detectionClasses]
    .sort(compareClasses)
    .map((detectionClass): OilSpillLegendGroup => ({
      id: `${idPrefix}:detection:${detectionClass}`,
      label: `Detection ${detectionClass}`,
      color: config?.detectionColorMap?.[detectionClass]?.color ?? fallbackColor,
      forecasts: [...(forecastHoursByClass.get(detectionClass) ?? [])]
        .sort((left, right) => left - right)
        .map((hour) => ({
          hour,
          color:
            config?.classForecastColorMap?.[detectionClass]?.[hour]?.color ??
            config?.forecastColorMap?.[hour]?.color ??
            fallbackColor,
        })),
    }));

  if (hasUnclassifiedDetection || unclassifiedForecastHours.size > 0) {
    groups.push({
      id: `${idPrefix}:detection:unclassified`,
      label: 'Detection',
      color: fallbackColor,
      forecasts: [...unclassifiedForecastHours]
        .sort((left, right) => left - right)
        .map((hour) => ({
          hour,
          color: config?.forecastColorMap?.[hour]?.color ?? fallbackColor,
        })),
    });
  }

  return groups;
}
